import { Skeleton } from '@systemeio/components'
import { CurrencyEnum } from '@systemeio/types'
import { AffiliateDashboardStatisticInterface } from 'modules/affiliate-dashboard/types/affiliate-dashboard-statistic-interface'
import { AffiliateStatisticCommonInterface } from 'modules/affiliate-program/affiliate/types/affiliate-interface'
import {
  getAffiliateSum,
  getAffiliateTotalSum,
} from 'modules/affiliate-program/utils/get-affiliate-sum'
import React, { useEffect, useState, type JSX } from 'react'
import { Hint } from 'shared/components'
import { CopyInputValueButton } from 'shared/components/copy-input-value-button'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'

interface AffiliateStatisticProps {
  affiliateStatistic?: AffiliateStatisticCommonInterface
  selectedCurrency?: CurrencyEnum
  isDashboard?: boolean
}

const AffiliateStatistic = ({
  affiliateStatistic,
  selectedCurrency,
  isDashboard = false,
}: AffiliateStatisticProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()

  const [mainCurrency, setMainCurrency] = useState<CurrencyEnum | undefined>()

  useEffect(() => {
    if (!affiliateStatistic) return

    const allCurrencies = Object.keys(affiliateStatistic.total) as CurrencyEnum[]
    if (selectedCurrency) {
      setMainCurrency(undefined)
    } else {
      setMainCurrency(
        allCurrencies[
          Object.values(affiliateStatistic.total).reduce(
            (iMax, x, i, arr) => (typeof arr[iMax] !== 'undefined' && x > arr[iMax] ? i : iMax),
            0,
          )
        ],
      )
    }
  }, [selectedCurrency, affiliateStatistic])

  return (
    <div className={'flex flex-col gap-5'}>
      <div
        className={
          'bg-bg-primary border-border-100 flex w-full flex-col overflow-x-hidden rounded-lg border'
        }
      >
        <span className={'text-text-primary px-6 py-4 text-[17px] font-bold'}>
          {t('dashboard.affiliate.earnings')}
        </span>
        <div className={'flex flex-col px-6 py-4'}>
          <StatisticWrapper title={t('dashboard.affiliate.today')} isFirst>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.today,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.today,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper title={t('dashboard.affiliate.last_7_days')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.last7Days,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.last7Days,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper title={t('dashboard.affiliate.last_30_days')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.last30Days.sums,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.last30Days.sums,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper title={t('dashboard.affiliate.paid')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.paidSums,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.paidSums,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper title={t('dashboard.affiliate.pending')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.pendingSums,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.pendingSums,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper isLast title={t('dashboard.affiliate.lifetime_earnings')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.total,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.total,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
        </div>
      </div>
      <div
        className={
          'bg-bg-primary border-border-100 flex w-full flex-col gap-3 overflow-x-hidden rounded-lg border px-6 py-4'
        }
      >
        {isDashboardStatistic(isDashboard, affiliateStatistic) ? (
          <div className={'flex w-full justify-between gap-1.5'}>
            <span className={'text-text-primary whitespace-nowrap'}>
              {t('dashboard.affiliate_dashboard.your_affiliate_id')}
            </span>
            <div className={'flex w-full justify-end gap-1.5 truncate'}>
              <StatisticContent affiliateStatistic={affiliateStatistic}>
                {affiliateStatistic => affiliateStatistic.user.affiliateAccount.affiliateId}
              </StatisticContent>
              {affiliateStatistic && (
                <CopyInputValueButton text={affiliateStatistic.user.affiliateAccount.affiliateId} />
              )}
              <Hint label={t('dashboard.affiliate_dashboard.affiliate_id_tooltip')} />
            </div>
          </div>
        ) : (
          <div className={'flex w-full justify-between gap-1.5'}>
            <span className={'text-text-primary whitespace-nowrap'}>
              {t('dashboard.affiliate.affiliate_id')}
            </span>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic => affiliateStatistic.contact.affiliateAccount.affiliateId}
            </StatisticContent>
          </div>
        )}
        <div className={'flex w-full justify-between gap-1.5'}>
          <span className={'text-text-primary'}>{t('dashboard.affiliate.total_leads')}</span>
          <StatisticContent affiliateStatistic={affiliateStatistic}>
            {affiliateStatistic => affiliateStatistic.totalLeads}
          </StatisticContent>
        </div>
      </div>
    </div>
  )
}

const StatisticWrapper = ({
  title,
  isLast,
  isFirst,
  children,
}: {
  title: string
  isLast?: boolean
  isFirst?: boolean
  children: JSX.Element
}) => (
  <div
    className={`flex justify-between gap-1.5 ${isFirst && 'pt-0'} ${
      isLast ? 'pt-5' : 'border-b-border-200 border-b py-5'
    } items-center`}
  >
    <span className={'text-text-primary'}>{title}</span>
    {children}
  </div>
)

const isDashboardStatistic = (
  isDashboard: boolean,
  stats?: AffiliateStatisticCommonInterface,
): stats is AffiliateDashboardStatisticInterface => isDashboard

const StatisticContent = <T,>({
  affiliateStatistic,
  children,
}: {
  affiliateStatistic?: T
  children: (affiliateStatistic: T) => JSX.Element | string | number
}) =>
  affiliateStatistic ? (
    <span className={'text-text-primary truncate font-bold'}>{children(affiliateStatistic)}</span>
  ) : (
    <Skeleton className="rounded-lg px-5 text-transparent">.</Skeleton>
  )

export default AffiliateStatistic
